/* Günlük — yazı listesi ve yazı editörü.

   Liste: başlık, kategori, durum (yayında / taslak), düzenle, sil, yeni yazı.
   Yazı aynı yerinde düzenleyicide açılıyor (editor/shell.ts): önizleme
   yazının kendi sayfası (`post.html`), gövde yerinde yazılıyor; başlık,
   giriş, kapak, tarih gibi bilgiler sağ panelde.

   Kayıt iki parça: gövde taslağı (`PUT …/draft`) ve bilgiler (`PATCH`).
   "Canlıya al" gövdeyi canlıya kopyalayıp yayını tetikliyor. Yayın tarihi
   olmayan yazı taslaktır ve siteye basılmaz (tools/src/content.ts).

   Blokları olmayan eski bir yazı ilk açılışta `body_html`'den bloklara
   çevriliyor (editor/import.ts); kaydedilene kadar veritabanında hiçbir
   şey değişmiyor. */

import { postBodySchema, type Block, type Design, type PageSeo, type Rich } from '@frawgraphy/shared';
import type { PhotoRow, PostRow, SiteContent } from '@frawgraphy/tools/content';
import { design, posts, type PostRecord } from '../api.ts';
import { el, replace, when } from '../dom.ts';
import type { AppContext } from '../main.ts';
import { blankBlock } from '../editor/blank.ts';
import { group, mediaField, textField, toggleField } from '../editor/fields.ts';
import { htmlToBlocks } from '../editor/import.ts';
import { previewPage } from '../editor/page.ts';
import { mountEditor, type EditorSource } from '../editor/shell.ts';
import { takenIds } from '../editor/doc.ts';

type Meta = {
  slug: string;
  title: Rich;
  titleHtml: Rich;
  shortTitle: Rich;
  lede: Rich;
  excerpt: Rich;
  category: Rich;
  readMinutes: number;
  publishedAt: string | null;
  coverPhotoId: string | null;
  seo: PageSeo | null;
};

/**
 * Yazının SEO bilgisi; girilmemişse başlık ve özetten türetilir. Sayfa
 * çizimi (view.ts `seoOf`) dört alanı da zorunlu tutuyor — SEO'su boş bir
 * yazı yayımlansa derleme düşerdi.
 */
function seoFor(meta: Pick<Meta, 'title' | 'excerpt' | 'seo'>): PageSeo {
  const plain = (r: Rich) => ({ tr: r.tr.replace(/<[^>]+>/g, '') });
  return {
    docTitle: meta.seo?.docTitle ?? { tr: `${plain(meta.title).tr} — Frawgraphy Günlük` },
    description: meta.seo?.description ?? plain(meta.excerpt),
    ogTitle: meta.seo?.ogTitle ?? plain(meta.title),
    ogDescription: meta.seo?.ogDescription ?? plain(meta.excerpt),
    ...(meta.seo?.keywords ? { keywords: meta.seo.keywords } : {}),
    ogImage: meta.seo?.ogImage ?? null
  };
}

let cache: { content: SiteContent; design: Design } | null = null;

export async function renderJournal(host: HTMLElement, ctx: AppContext): Promise<void> {
  replace(host, el('h1', { text: 'Günlük' }), el('p', { class: 'note', text: 'Yazılar okunuyor…' }));
  let rows: PostRecord[];
  try {
    rows = (await posts.list()).posts;
  } catch (err) {
    ctx.fail(err);
    return;
  }

  const create = async () => {
    const n = rows.length + 1;
    const today = new Date().toISOString().slice(0, 10);
    try {
      const created = await posts.create({
        slug: `yeni-yazi-${Date.now().toString(36)}`,
        title: { tr: `Yeni yazı ${n}` },
        shortTitle: { tr: `Yeni yazı ${n}` },
        titleHtml: { tr: `Yeni <em>yazı</em> ${n}` },
        lede: { tr: 'Yazının giriş cümlesi.' },
        excerpt: { tr: 'Listede görünen kısa özet.' },
        bodyHtml: { tr: '<p>Yazının ilk paragrafı.</p>' },
        category: { tr: 'Günlük' },
        readMinutes: 3,
        seo: seoFor({ title: { tr: `Yeni yazı ${n}` }, excerpt: { tr: 'Listede görünen kısa özet.' }, seo: null }),
        publishedAt: null
      });
      ctx.toast(`Taslak oluşturuldu (${today}). Yayımlamak için sağ paneldeki "Yayında" kutusu.`);
      await openPost(host, ctx, created.id);
    } catch (err) {
      ctx.fail(err);
    }
  };

  replace(
    host,
    el('div', { class: 'row' }, el('h1', { text: 'Günlük', style: 'margin:0' }), el('span', { class: 'spacer' }), el('button', { type: 'button', class: 'primary', text: 'Yeni yazı', onclick: () => void create() })),
    el('div', { style: 'height:14px' }),
    rows.length === 0
      ? el('p', { class: 'note', text: 'Henüz yazı yok.' })
      : el(
          'table',
          {},
          el('thead', {}, el('tr', {}, el('th', { text: 'yazı' }), el('th', { text: 'kategori' }), el('th', { text: 'durum' }), el('th', { text: 'gövde' }), el('th', { text: '' }))),
          el(
            'tbody',
            {},
            ...rows.map((r) =>
              el(
                'tr',
                {},
                el('td', {}, el('a', { href: '#', text: r.title.tr, style: 'color:inherit', onclick: (e: Event) => { e.preventDefault(); void openPost(host, ctx, r.id); } })),
                el('td', { class: 'dim', text: r.category.tr }),
                el('td', { class: r.publishedAt ? 'ok' : 'dim', text: r.publishedAt ? `yayında · ${when(r.publishedAt)}` : 'taslak' }),
                el('td', { class: 'dim', text: r.blocks ? 'bloklar' : r.blocksDraft ? 'taslak bloklar' : 'eski HTML' }),
                el(
                  'td',
                  { style: 'text-align:right' },
                  el('button', { type: 'button', text: 'Düzenle', onclick: () => void openPost(host, ctx, r.id) }),
                  ' ',
                  el('button', {
                    type: 'button',
                    class: 'danger',
                    text: 'Sil',
                    onclick: async () => {
                      if (!confirm(`"${r.title.tr}" kalıcı olarak silinsin mi? Geri alınamaz.`)) return;
                      try {
                        await posts.remove(r.id);
                        ctx.toast('Yazı silindi. Siteden kalkması için yayınlayın.');
                        await renderJournal(host, ctx);
                      } catch (err) {
                        ctx.fail(err);
                      }
                    }
                  })
                )
              )
            )
          )
        )
  );
}

async function openPost(host: HTMLElement, ctx: AppContext, id: string): Promise<void> {
  replace(host, el('h1', { text: 'Günlük' }), el('p', { class: 'note', text: 'Yazı açılıyor…' }));
  let record: PostRecord;
  try {
    const [r, c, d] = await Promise.all([posts.read(id), cache ? Promise.resolve(cache.content) : design.content(), cache ? Promise.resolve(null) : design.read()]);
    record = r;
    if (!cache) cache = { content: c, design: d?.draft.doc ?? { version: 1, tokens: { dark: {}, light: {} } } };
  } catch (err) {
    ctx.fail(err);
    return;
  }
  const { content, design: siteDesign } = cache;

  const parseBody = (raw: unknown[] | null): Block[] | null => (raw ? postBodySchema.safeParse(raw).data ?? null : null);
  const imported = htmlToBlocks(record.bodyHtml?.tr ?? '');
  const liveBody = parseBody(record.blocks) ?? imported;
  const draftBody = parseBody(record.blocksDraft) ?? liveBody;

  const metaOf = (r: PostRecord): Meta => ({
    slug: r.slug,
    title: r.title,
    titleHtml: r.titleHtml ?? r.title,
    shortTitle: r.shortTitle ?? r.title,
    lede: r.lede ?? { tr: '' },
    excerpt: r.excerpt ?? { tr: '' },
    category: r.category,
    readMinutes: r.readMinutes,
    publishedAt: r.publishedAt,
    coverPhotoId: r.coverPhotoId,
    seo: r.seo
  });

  const s = {
    meta: metaOf(record),
    body: structuredClone(draftBody),
    savedMeta: metaOf(record),
    savedBody: draftBody,
    liveBody
  };
  const snap = (meta: Meta, body: Block[]) => JSON.stringify({ meta, body });

  /** Yazı gövdesi editörde tek bir kabın içinde: bloklar en üstte durmuyor (doc.ts kuralları). */
  const ROOT_ID = 'yazi-govdesi';
  const root = (): Block[] => [{ id: ROOT_ID, type: 'page', head: [], blocks: s.body }];

  const photoById = (pid: string | null): PhotoRow | null => {
    if (!pid) return null;
    const all = [...content.featured, ...content.works.map((w) => w.photo), ...content.albums.flatMap((a) => a.photos), ...content.posts.flatMap((p) => (p.coverPhoto ? [p.coverPhoto] : []))];
    return all.find((p) => p.id === pid) ?? null;
  };

  const previewContent = (): SiteContent => {
    const row: PostRow = {
      slug: s.meta.slug,
      title: s.meta.title,
      shortTitle: s.meta.shortTitle,
      titleHtml: s.meta.titleHtml,
      lede: s.meta.lede,
      excerpt: s.meta.excerpt,
      bodyHtml: record.bodyHtml ?? { tr: '' },
      category: s.meta.category,
      readMinutes: s.meta.readMinutes,
      cover: record.cover as PostRow['cover'],
      coverPhoto: photoById(s.meta.coverPhotoId),
      seo: seoFor(s.meta),
      publishedAt: s.meta.publishedAt ?? new Date().toISOString().slice(0, 10),
      blocks: s.body
    };
    return { ...content, posts: [row, ...content.posts.filter((p) => p.slug !== record.slug && p.slug !== s.meta.slug)] };
  };

  const source: EditorSource = {
    title: s.meta.shortTitle.tr || 'Yazı',
    root,
    apply: (next) => {
      const container = next.find((b) => b.id === ROOT_ID);
      if (!container || container.type !== 'page') return 'Yazı gövdesi bulunamadı.';
      const parsed = postBodySchema.safeParse(container.blocks);
      if (!parsed.success) return parsed.error.issues[0]?.message ?? 'Geçersiz değişiklik.';
      s.body = parsed.data;
      return null;
    },
    preview: () => previewPage(siteDesign, previewContent(), 'post', s.meta.slug),
    snapshot: () => snap(s.meta, s.body),
    restore: (value) => {
      const v = JSON.parse(value) as { meta: Meta; body: Block[] };
      s.meta = v.meta;
      s.body = v.body;
    },
    savedSnapshot: () => snap(s.savedMeta, s.savedBody),
    liveSnapshot: () => snap(s.savedMeta, s.liveBody),
    save: async () => {
      const patch: Record<string, unknown> = {};
      for (const key of Object.keys(s.meta) as (keyof Meta)[]) {
        if (JSON.stringify(s.meta[key]) !== JSON.stringify(s.savedMeta[key])) patch[key] = s.meta[key];
      }
      /* SEO'su hiç girilmemiş eski bir yazıyı yayımlamak derlemeyi düşürmesin. */
      if (!s.savedMeta.seo) patch['seo'] = seoFor(s.meta);
      if (Object.keys(patch).length) await posts.patch(record.id, patch);
      await posts.saveDraft(record.id, s.body);
      s.savedMeta = structuredClone(s.meta);
      s.savedBody = structuredClone(s.body);
    },
    promote: async () => {
      await posts.promote(record.id);
      s.liveBody = structuredClone(s.body);
    },
    sidePanel: (panel, ictx, change) => {
      const set = (fn: (m: Meta) => void) => change(() => fn(s.meta));
      const loc = (label: string, key: 'title' | 'titleHtml' | 'shortTitle' | 'lede' | 'excerpt' | 'category', hint: string) =>
        textField(label, s.meta[key].tr.replace(/&amp;/g, '&'), hint, (v) => set((m) => (m[key] = { ...m[key], tr: v })));
      const addBody = (type: 'paragraph' | 'heading' | 'image' | 'quote' | 'list' | 'table') =>
        ictx.mutate((all) => {
          const container = all.find((b) => b.id === ROOT_ID);
          if (container?.type !== 'page') return null;
          const block = blankBlock(type, takenIds(all));
          container.blocks.push(block);
          return block.id;
        });
      replace(
        panel,
        el('p', { class: 'tile-name', text: 'Yazının bilgileri' }),
        el('p', { class: 'dim', text: 'Gövdeyi önizlemede yazın; bir bloğa tıklayınca ayarları burada açılır.' }),
        group(
          'Yayın',
          toggleField('Yayında (sitede görünür)', Boolean(s.meta.publishedAt), (on) => set((m) => (m.publishedAt = on ? m.publishedAt ?? new Date().toISOString().slice(0, 10) : null))),
          s.meta.publishedAt
            ? textField('Yayın tarihi', s.meta.publishedAt, '2026-10-18', (v) =>
                /^\d{4}-\d{2}-\d{2}$/.test(v) ? set((m) => (m.publishedAt = v)) : ictx.toast('Tarih YYYY-AA-GG biçiminde olmalı.', 'bad')
              )
            : null,
          textField('Adres (slug)', s.meta.slug, 'tek-isikla-editorial', (v) =>
            /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(v) ? set((m) => (m.slug = v)) : ictx.toast('Slug küçük harf, rakam ve tire.', 'bad')
          ),
          textField('Okuma süresi (dk)', String(s.meta.readMinutes), '5', (v) => {
            const n = Number(v);
            if (Number.isInteger(n) && n > 0 && n <= 120) set((m) => (m.readMinutes = n));
            else ictx.toast('Okuma süresi 1–120 arası bir sayı olmalı.', 'bad');
          })
        ),
        group(
          'Başlık ve metinler',
          loc('Başlık', 'title', 'Tek ışıkla editorial'),
          loc('Sayfadaki başlık (<em> ile vurgu)', 'titleHtml', 'Tek ışıkla <em>editorial</em>'),
          loc('Kısa başlık (gezinme)', 'shortTitle', 'Tek ışık'),
          loc('Giriş', 'lede', 'Yazının giriş cümlesi'),
          loc('Özet (listede)', 'excerpt', 'Kısa özet'),
          loc('Kategori', 'category', 'Işık')
        ),
        group(
          'Kapak',
          mediaField('Kapak karesi (arşivden)', s.meta.coverPhotoId ? { photo: s.meta.coverPhotoId } : undefined, (m) => set((meta) => (meta.coverPhotoId = m?.photo ?? null)), ictx.fail)
        ),
        group(
          'Arama motorları',
          textField('Sekme başlığı', seoFor(s.meta).docTitle.tr, 'Yazı — Frawgraphy Günlük', (v) =>
            set((m) => (m.seo = { ...seoFor(m), docTitle: { tr: v || seoFor({ ...m, seo: null }).docTitle.tr } }))
          ),
          textField('Arama açıklaması', seoFor(s.meta).description.tr, 'Arama sonucunda görünen iki satır', (v) =>
            set((m) => (m.seo = { ...seoFor(m), description: { tr: v || m.excerpt.tr }, ogDescription: { tr: v || m.excerpt.tr } }))
          ),
          textField('Paylaşım başlığı', seoFor(s.meta).ogTitle.tr, 'Sosyal medyada görünen başlık', (v) =>
            set((m) => (m.seo = { ...seoFor(m), ogTitle: { tr: v || m.title.tr } }))
          )
        ),
        group(
          'Gövdeye ekle',
          el(
            'div',
            { class: 'row' },
            el('button', { type: 'button', text: '+ Paragraf', onclick: () => addBody('paragraph') }),
            el('button', { type: 'button', text: '+ Başlık', onclick: () => addBody('heading') }),
            el('button', { type: 'button', text: '+ Görsel', onclick: () => addBody('image') }),
            el('button', { type: 'button', text: '+ Alıntı', onclick: () => addBody('quote') }),
            el('button', { type: 'button', text: '+ Liste', onclick: () => addBody('list') }),
            el('button', { type: 'button', text: '+ Tablo', onclick: () => addBody('table') })
          ),
          record.blocks || record.blocksDraft ? null : el('p', { class: 'dim', text: 'Bu yazı eski HTML gövdeden bloklara çevrildi; kaydedene kadar veritabanında hiçbir şey değişmez.' })
        )
      );
    }
  };

  mountEditor(host, ctx, source, { back: () => void renderJournal(host, ctx) });
}
